"use client";

import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { visualViewportRect } from "@/lib/anchoredPosition";

interface ImageCropperProps {
  src: string;
  aspect: number; // width/height, e.g. 1 for square, 16/9 for cover
  onCrop: (blob: Blob) => void;
  onCancel: () => void;
}

/**
 * The export format is the cropper's, not the call site's, so the call site
 * must not name the file. `prepareUpload` (backend `modules/uploads/hygiene.ts`)
 * takes the stored extension, the served `Content-Type` and the rendition
 * encoder from the filename, never from the bytes. All five call sites
 * hard-coded `"cover.png"` / `"image/png"`, true only while `handleCrop` always
 * emitted PNG; now that a cover comes back as JPEG, that literal would store
 * JPEG bytes as `.png`, serve them as `image/png` and re-encode every thumbnail
 * through the PNG encoder.
 *
 * `base` is the name without an extension ("cover", "avatar").
 */
export function croppedFile(blob: Blob, base: string): File {
  const type = blob.type || "image/png";
  const extension = type === "image/jpeg" ? "jpg" : "png";
  return new File([blob], `${base}.${extension}`, { type });
}

/**
 * How big the crop box may get (OSO-551). It used to be a flat 280 px along
 * whichever side the aspect made the long one, phone and 27" monitor alike — so
 * a 3:1 group cover was framed in a 280 × 93 strip, and this box is the only
 * look at the image anyone gets before it is saved.
 *
 * The numbers below restate the panel's own Tailwind classes in JS: a canvas
 * needs a pixel size before it can be laid out, so there is nothing to measure
 * until one is picked. Keep them in step with the classNames at the bottom of
 * the file — a few px out costs a few px of box and nothing worse.
 */
const BACKDROP_PADDING = 32; // `p-4` on the scroll wrapper, both sides
const PANEL_PADDING = 48; // `p-6` on the panel, both sides
// Everything in the panel that is not the box: title, zoom slider, hint line,
// the low-res line when it shows, the buttons, and the panel's own padding.
const PANEL_CHROME_HEIGHT = 300;
// Past this the box stops helping you frame and starts making the dialog a
// page. 640 × 213 for a 3:1 cover is more than five times the old area.
const MAX_BOX = { w: 640, h: 420 };
/**
 * The size the box always used to be, and still three of the things that size
 * was doing implicitly: the value before the window has been measured (and
 * under SSR), the FLOOR — a landscape phone or a desktop with devtools open
 * has less height than the panel wants, and the box never gets smaller than
 * it was; the backdrop scrolls instead — and the frame the low-res warning is
 * calibrated in, see the loader.
 */
const BASE_BOX = 280;

interface Size {
  w: number;
  h: number;
}

/** The largest box of this `aspect` that fits inside `maxW` × `maxH`. */
function fitViewport(aspect: number, maxW: number, maxH: number): Size {
  const w = Math.round(Math.min(maxW, maxH * aspect));
  return { w, h: w / aspect };
}

/** The smallest scale at which `img` covers `box` — `object-fit: cover`. */
function coverScale(box: Size, img: { width: number; height: number }): number {
  return Math.max(box.w / img.width, box.h / img.height);
}

/**
 * Measured off the viewport, not off a laid-out element: see above for why
 * there is nothing to observe yet. It also makes the box a pure function of
 * the viewport, which is what lets the resize handler be a single `setState`.
 *
 * Width is `documentElement.clientWidth`, not `innerWidth`: a `fixed inset-0`
 * element is laid out in the viewport MINUS a classic scrollbar, and the
 * backdrop grows one of its own on a short window. Height is the visual
 * viewport, which is the one that shrinks for the software keyboard on iOS —
 * `innerHeight` does not (see `visualViewportRect`).
 */
function measureViewportWidth(aspect: number): number {
  const floor = fitViewport(aspect, BASE_BOX, BASE_BOX);
  if (typeof document === "undefined") return floor.w;
  const viewport = visualViewportRect();
  const maxW = Math.max(
    1,
    Math.min(document.documentElement.clientWidth - BACKDROP_PADDING - PANEL_PADDING, MAX_BOX.w)
  );
  const maxH = Math.min(
    Math.max(viewport.bottom - viewport.top - PANEL_CHROME_HEIGHT, floor.h),
    MAX_BOX.h
  );
  return fitViewport(aspect, maxW, maxH).w;
}

export function ImageCropper({ src, aspect, onCrop, onCancel }: ImageCropperProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgSize, setImgSize] = useState<{ width: number; height: number } | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Scale and offset are one value because nothing changes one without at
  // least reading the other: zoom moves the offset to keep the centre put, a
  // resize multiplies both. One functional update is also what lets the
  // native wheel listener below zoom without re-subscribing every render.
  const [crop, setCrop] = useState({ scale: 1, x: 0, y: 0 });
  // The source needs noticeable upscaling just to cover the crop viewport —
  // a good sign the final image will look soft/pixelated once displayed at
  // full width (OSO-371). A soft warning, not a block: a small photo is
  // still better than none.
  const [lowRes, setLowRes] = useState(false);
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  /**
   * Escape cancels the crop, and is spent doing it.
   *
   * At one of the four call sites — `EventComposer` — this overlay is a DOM
   * child of a `Dialog` panel (which is what keeps Tab from being pulled back
   * into the form behind it), and `Dialog` closes on any Escape reaching it
   * un-`preventDefault()`ed: with no handler here, Escape while cropping asked
   * the composer to discard the half-written event. The other three
   * (`GroupHeader`, the profile page, group creation) render it standalone,
   * where Escape did nothing. The key means "cancel the crop" at all four,
   * which is why the handler lives here and not in the composer.
   *
   * Capture phase is what makes `preventDefault()` — the same "handled" signal
   * `useDismissable` sends — arrive first: both listeners sit on `document`,
   * where bubble order is registration order, and the dialog was open before
   * this mounted. `onCancel` is read from a ref so an inline closure does not
   * re-subscribe.
   */
  const cancelRef = useRef(onCancel);
  useEffect(() => {
    cancelRef.current = onCancel;
  });
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      cancelRef.current();
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  // Viewport size for the crop area. One number in state, not a `{w,h}`
  // object: the height follows from the fixed aspect, and a primitive is what
  // lets React bail out of a resize that changed nothing, so the effect below
  // does not have to.
  const [viewportWidth, setViewportWidth] = useState(() => measureViewportWidth(aspect));
  const viewportSize = { w: viewportWidth, h: viewportWidth / aspect };

  useEffect(() => {
    // A rotation and a window being dragged fire on `window`; the visual
    // viewport is what fires when the software keyboard opens on iOS (and on
    // a pinch-zoom) — the same pair `useAnchoredPosition` listens to. One
    // frame per burst: a drag fires dozens of events between paints.
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setViewportWidth(measureViewportWidth(aspect)));
    };
    const vv = window.visualViewport;
    window.addEventListener("resize", onResize);
    vv?.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      vv?.removeEventListener("resize", onResize);
    };
  }, [aspect]);

  /**
   * A resize moves the box, not the crop (OSO-551).
   *
   * `scale` and `offset` are both in box pixels, so a box growing by k describes
   * the same region of the source only if both are multiplied by k. The aspect
   * is fixed, so every resize is exactly one uniform k and the framing always
   * carries over. Everything derived follows: `minScale` and the slider are
   * ratios, and the export is `viewportSize.w / scale` source pixels, which k
   * cancels out of.
   *
   * Without this the loader below would re-run on every size change to re-fit
   * the image, as it was written — and re-decoding the source to throw away the
   * user's framing because the keyboard opened is a worse bug than the small box
   * this ticket is about.
   *
   * A layout effect: the draw effect below depends on the box size too and would
   * otherwise paint the new box once with the old scale before this correction
   * lands. `committedWidth` is the width the box actually has on screen, which
   * the loader and the zoom helper read instead of render-scope state so neither
   * depends on it.
   */
  const committedWidth = useRef(viewportWidth);
  useLayoutEffect(() => {
    const k = viewportWidth / committedWidth.current;
    committedWidth.current = viewportWidth;
    if (k === 1) return;
    setCrop((prev) => ({ scale: prev.scale * k, x: prev.x * k, y: prev.y * k }));
  }, [viewportWidth]);

  const committedBox = useCallback(
    (): Size => ({ w: committedWidth.current, h: committedWidth.current / aspect }),
    [aspect]
  );

  // Load image — keyed on the source alone, see above.
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      setImgSize({ width: img.width, height: img.height });
      // Fit image to fill the viewport
      const box = committedBox();
      const fitScale = coverScale(box, img);
      setCrop({
        scale: fitScale,
        x: (box.w - img.width * fitScale) / 2,
        y: (box.h - img.height * fitScale) / 2,
      });
      // The warning keeps the box it was calibrated against: OSO-371 picked
      // `fitScale > 1.5` when the box was always 280 px, and it warns about the
      // saved image, which the preview size does not touch. Measured against
      // the live box, the same photo would read low-res on a 27" monitor and
      // fine on a phone.
      setLowRes(coverScale(fitViewport(aspect, BASE_BOX, BASE_BOX), img) > 1.5);
      setLoaded(true);
    };
    img.src = src;
  }, [src, aspect, committedBox]);

  // Draw. The backing store is CSS px × devicePixelRatio: at 640 px wide a
  // 1× store on a 2× display is upscaled to 1280 and "too small" turns into
  // "big and soft". The CSS size stays in `style` below, and the store is only
  // resized when it actually changes, because assigning `canvas.width` wipes
  // the bitmap and resets the context — on every drag frame, otherwise.
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const storeW = Math.round(viewportSize.w * dpr);
    const storeH = Math.round(viewportSize.h * dpr);
    if (canvas.width !== storeW) canvas.width = storeW;
    if (canvas.height !== storeH) canvas.height = storeH;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.clearRect(0, 0, viewportSize.w, viewportSize.h);
    ctx.fillStyle = "#f3f4f6";
    ctx.fillRect(0, 0, viewportSize.w, viewportSize.h);
    ctx.drawImage(img, crop.x, crop.y, img.width * crop.scale, img.height * crop.scale);

    // Draw circular overlay for square aspect (avatar)
    if (aspect === 1) {
      ctx.save();
      ctx.globalCompositeOperation = "destination-in";
      ctx.beginPath();
      ctx.arc(viewportSize.w / 2, viewportSize.h / 2, viewportSize.w / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }, [crop, viewportSize.w, viewportSize.h, aspect]);

  useEffect(() => {
    if (loaded) draw();
  }, [loaded, draw]);

  // Mouse/touch handlers
  const handlePointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setCrop((prev) => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
  };

  const handlePointerUp = () => {
    dragging.current = false;
  };

  // Zoom toward the centre of the box, clamped to [cover, 5 × cover]. `pick`
  // gets the cover scale and the current one, so the wheel can be relative
  // and the slider absolute.
  const zoomTo = useCallback(
    (pick: (minScale: number, current: number) => number) => {
      const img = imgRef.current;
      if (!img) return;
      const box = committedBox();
      const minScale = coverScale(box, img);
      setCrop((prev) => {
        const scale = Math.max(minScale, Math.min(pick(minScale, prev.scale), minScale * 5));
        const cx = box.w / 2;
        const cy = box.h / 2;
        return {
          scale,
          x: cx - (cx - prev.x) * (scale / prev.scale),
          y: cy - (cy - prev.y) * (scale / prev.scale),
        };
      });
    },
    [committedBox]
  );

  // A native listener, not `onWheel`: React registers `wheel` passively, so
  // `preventDefault()` in the prop is ignored and the wheel scrolls the
  // backdrop — which it can, now that it is `overflow-y-auto`.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomTo((_, current) => current * (e.deltaY > 0 ? 0.95 : 1.05));
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, [zoomTo]);

  const handleZoomSlider = (val: number) => zoomTo((minScale) => minScale + minScale * 4 * val);

  const handleCrop = () => {
    const canvas = document.createElement("canvas");
    const img = imgRef.current;
    if (!img) return;

    /**
     * Target the surface, not the preview (OSO-436).
     *
     * This used to be `aspect === 1 ? 400 : viewportSize.w * 2` — and
     * `viewportSize.w` is 280, so a group cover uploaded at 560 × 186, then
     * blown up to the full width of the main column: a 1.36× upscale on a 1×
     * display and 2.7× on a 2× one. That upscale is the reported grain, and
     * the server never saw anything better — it stores what it is given
     * (`UPLOADS_MAX_IMAGE_DIMENSION` is 0) and covers are served at `full`.
     *
     * Since OSO-590 the header band is `aspect-[3/1]`, so WIDTH is the only
     * thing that binds and `object-cover` crops nothing. Before that the band
     * was a fixed `h-48 lg:h-72` and was squarer than the crop, so height
     * bound and the sides were cut — which is the mismatch OSO-590 removed.
     *
     * 2048 covers a 1024 CSS px column at 2× DPR. 1024 for an avatar is the
     * ceiling worth sending: the largest avatar rendition is `s512` and
     * `renderVariant` resizes `withoutEnlargement`, so anything above that is
     * bytes nobody ever decodes.
     *
     * Keep the 2048 equal to `COVER_SIZE` in
     * `backend/src/modules/media/variants.ts`, the server-side ceiling covers
     * are served at (OSO-426) — same number, same reason, in two files that
     * cannot import from each other. The coupling is soft both ways (above it
     * the server downscales, below it `withoutEnlargement` passes the bytes
     * through), so this is a number to keep tidy, not an invariant to defend.
     */
    const desiredSize = aspect === 1 ? 1024 : 2048;
    // Cap at the source's own resolution within the cropped region
    // (viewportSize.w / scale native pixels) — desiredSize is a good target
    // for a large photo, but for a small one it just compounds the upscale
    // already applied to fill the viewport, producing a blurrier result
    // than the source ever had (OSO-371).
    const outputSize = Math.max(1, Math.min(desiredSize, viewportSize.w / crop.scale));
    canvas.width = outputSize;
    canvas.height = outputSize / aspect;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    /**
     * PNG for the avatar, JPEG for the cover.
     *
     * The old call was `toBlob(…, "image/png", 0.95)`, whose quality argument is
     * silently ignored — PNG is lossless. At 560 px that only wasted bytes; at
     * 2048 px a photographic PNG cover is several megabytes, so raising the
     * resolution without changing format would trade grain for weight. JPEG q0.9
     * at 2048 × 682 measured ~380 KB against ~3.8 MB as PNG, well inside the
     * 10 MB image limit (`UPLOADS_MAX_IMAGE_SIZE_KB`).
     *
     * The avatar stays PNG: it is often a logo or flat-colour mark, where JPEG
     * ringing shows and PNG is the smaller of the two anyway.
     */
    const type = aspect === 1 ? "image/png" : "image/jpeg";

    // JPEG has no alpha and the canvas spec composites onto black. Dragging
    // the image past an edge leaves uncovered canvas, so without this a cover
    // picks up black bars where the preview showed the viewport's grey.
    if (type === "image/jpeg") {
      ctx.fillStyle = "#f3f4f6";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // A 12 MP source into a 2048 px canvas is a large single-step downscale,
    // which browsers alias unless asked not to. This is the cheap half of the
    // fix; halving passes would be the expensive one, and are not needed yet.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const scaleRatio = outputSize / viewportSize.w;
    ctx.drawImage(
      img,
      crop.x * scaleRatio,
      crop.y * scaleRatio,
      img.width * crop.scale * scaleRatio,
      img.height * crop.scale * scaleRatio
    );

    canvas.toBlob(
      (blob) => {
        if (blob) onCrop(blob);
      },
      type,
      0.9
    );
  };

  // Dimensions come from state, not imgRef: the ref is for the imperative
  // canvas work below, and reading it during render only happened to be safe
  // because `loaded` re-rendered right after it was assigned.
  const minScale = imgSize ? coverScale(viewportSize, imgSize) : 1;
  const maxScale = minScale * 5;
  const sliderVal = maxScale > minScale ? (crop.scale - minScale) / (maxScale - minScale) : 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm">
      {/* `min-h-full` + `items-center` keeps the panel centred while still
          letting the backdrop scroll when it cannot fit — a landscape phone,
          where the `BASE_BOX` floor deliberately takes more than there is.
          `items-center` on the backdrop itself would clip the top instead. */}
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Sized to the box rather than to `max-w-sm`, so the panel follows the
            crop area instead of the crop area being trapped inside it. The
            floor is for a portrait aspect, whose box is narrow enough to
            squeeze the two buttons — capped at the available width so it can
            never put the overflow back on a small phone. */}
        <div
          className="bg-surface rounded-2xl shadow-xl p-6 min-w-[min(280px,100%)] max-w-full"
          style={{ width: viewportSize.w + PANEL_PADDING }}
        >
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {aspect === 1 ? "Tilpas profilbillede" : "Tilpas coverbillede"}
          </h3>

          <div
            className="relative mx-auto overflow-hidden bg-muted rounded-xl"
            style={{ width: viewportSize.w, height: viewportSize.h }}
          >
            <canvas
              ref={canvasRef}
              style={{ width: viewportSize.w, height: viewportSize.h }}
              className="cursor-grab active:cursor-grabbing touch-none"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />

            {aspect === 1 && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `radial-gradient(circle at center, transparent ${viewportSize.w / 2 - 1}px, rgba(0,0,0,0.4) ${viewportSize.w / 2}px)`,
                }}
              />
            )}
          </div>

          <div className="flex items-center gap-3 mt-4 px-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-subtle-foreground shrink-0">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" />
            </svg>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={sliderVal}
              onChange={(e) => handleZoomSlider(parseFloat(e.target.value))}
              className="flex-1 accent-indigo-600"
            />
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-subtle-foreground shrink-0">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </div>

          <p className="text-xs text-subtle-foreground text-center mt-2">
            Træk for at flytte. Scroll eller brug slideren for at zoome.
          </p>

          {lowRes && (
            <p className="text-xs text-warning-muted-foreground text-center mt-2">
              Billedet er i lav opløsning og kan blive utydeligt. Vælg evt. et større billede.
            </p>
          )}

          <div className="flex gap-3 mt-5">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground bg-muted rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Annuller
            </button>
            <button
              onClick={handleCrop}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors"
            >
              Gem
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
